import React, { FC, useState } from "react";

import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";

import { SpinLoader } from "@/components/atoms";
import { Pagination } from "@/components/molecules";
import { claimApi } from "@/shared/apis";
import { defaultRelatedClaimPagination, Permissions, RelatedClaimType } from "@/shared/domain";
import { useAuth } from "@/shared/hooks";
import { StyledListEmpty } from "@/styles/styled";

import ClaimItem from "../ClaimItem/ClaimItem";

const defaultPage = { pageNumber: 1, pageSize: 5 };

type RelatedClaimsProps = {
    id: string;
    featureType: RelatedClaimType;
    ibpAclId: number;
    policyholderId: number;
    isSchoolContract?: boolean;
};

const RelatedClaims: FC<RelatedClaimsProps> = ({
    id,
    featureType,
    ibpAclId,
    policyholderId,
    isSchoolContract = false,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [page, setPage] = useState(defaultPage);
    const { user } = useAuth();

    const { data: claimsPaginated = defaultRelatedClaimPagination, isFetching } = useQuery(
        ["relatedClaims", { id, featureType, ...page }],
        () =>
            claimApi.getRelatedClaims(
                id,
                featureType,
                isSchoolContract,
                user.hasPermission(Permissions.ViewClosedClaim, user.policyHolderId),
                ibpAclId,
                policyholderId,
                page,
            ),
        {
            suspense: false,
            keepPreviousData: true,
        },
    );

    const { result: claims, total: totalItems } = claimsPaginated;
    const totalOfPages = Math.ceil(totalItems / page.pageSize);

    const handlePageSizeChange = (value: number) => {
        // Go back to the 1st page if page size changes
        setPage({ pageNumber: 1, pageSize: value });
        queryClient.invalidateQueries("relatedClaims");
    };

    return (
        <div style={{ width: "100%" }} data-testid="related_claims_list">
            {claims.map((claim) => (
                <ClaimItem
                    key={`claimitem-${claim.id}-${claim.reference}-${claim.insurer.fsma}`}
                    claim={claim}
                    simplified
                />
            ))}
            {claims.length === 0 && !isFetching && (
                <StyledListEmpty data-testid="empty-claims">{t("Claims.Empty")}</StyledListEmpty>
            )}
            {isFetching && <SpinLoader />}
            <Pagination
                tableMode={false}
                disabled={isFetching}
                page={page}
                pageCount={totalOfPages}
                onPageChange={(value: number) => setPage({ ...page, pageNumber: value })}
                onPageSizeChange={handlePageSizeChange}
            />
        </div>
    );
};

export default RelatedClaims;
